import api from './client'

export interface ChatAttachment {
  url: string
  name: string
  mime_type: string
  kind: 'image' | 'file'
}

export type GeneratedFileFormat =
  | 'xlsx' | 'docx' | 'pdf' | 'csv'
  | 'md' | 'txt' | 'json' | 'xml'
  | 'py' | 'php' | 'sql' | 'html' | 'css'
  | 'js' | 'ts' | 'jsx' | 'tsx' | 'yaml' | 'sh' | 'ps1'
  | 'go' | 'rs' | 'rb' | 'java' | 'c' | 'cpp' | 'cs' | 'ini' | 'toml'

export interface GeneratedFileInfo {
  url: string
  filename: string
  format: GeneratedFileFormat
  size: number
  content_type: string
}

export interface ChatRequest {
  message: string
  provider: string
  model?: string
  system_prompt?: string
  session_id?: string
  // Legacy: presigned URL'ы. Оставлены для совместимости — бэк всё ещё
  // парсит их если новые `*_attachments` не пришли.
  image_urls?: string[]
  file_urls?: string[]
  // Новый формат: явные S3-ключи + metadata. Если переданы — бэк
  // использует их напрямую, без парсинга URL'ов.
  image_attachments?: Array<{ key: string; mime_type?: string; filename?: string }>
  file_attachments?: Array<{ key: string; mime_type?: string; filename?: string }>
  attachments?: ChatAttachment[]
  agent_id?: string
  agent_title?: string
  agent_icon_url?: string
  context_mode?: string
  // Пользователь подтвердил, что готов провести запрос платно после того как
  // бесплатный дневной лимит закончился. Бэк без этого флага вернёт
  // limit_reached + next_request_paid; с ним — обработает и спишет.
  confirm_paid?: boolean
  // Подтверждение сметы (владелец 08.10): раз на чат при отправке в «∞» или с файлом, если сообщение обойдётся
  // больше 3× «от N». Без него бэк вернёт estimate_confirm и модель не запустит (Chat.tsx → estimatePrompt).
  confirm_estimate?: boolean
  // Правка фото в один шаг (21.10): человек заранее согласился в виджете на цену правки, когда фото столько, что
  // правка дороже сообщения больше допуска. Нейросеть не стала править — спишется цена сообщения.
  edit_consent_price?: number
}

// Текущее заполнение окна контекста после ответа.
// `used` — реальные input-токены от провайдера (не оценка),
// `limit` — потолок текущего режима памяти,
// `percent` — округлённый 0..100.
// `dropped_messages`/`dropped_tokens`/`trimmed` (TZ_context_infinity.md
// раздел 4) — сколько сообщений истории не поместилось в бюджет и было
// молча отброшено ЭТИМ запросом. trimmed=true — сигнал показать плашку
// (см. Chat.tsx) и строку в поповере режима памяти (ContextButton.tsx).
export interface ContextUsage {
  used: number
  limit: number
  percent: number
  dropped_messages?: number
  dropped_tokens?: number
  trimmed?: boolean
}

export interface ChatResponse {
  ok: boolean
  limit_reached?: boolean
  // Бесплатные запросы на сегодня исчерпаны, но следующий можно провести
  // платно. Фронт показывает подтверждение и пересылает тот же запрос с
  // confirm_paid:true (см. Chat.tsx submitChatRequest / paidPrompt).
  next_request_paid?: boolean
  content?: string
  tokens_input?: number
  tokens_output?: number
  credits_spent?: number
  error?: string
  /** Отказ нейросети, закончившийся предупреждением или списанием («Отказы нейросетей»): текст окна. */
  refusal?: import('./refusals').RefusalNotice | null
  session_id?: string
  image_urls?: string[]
  file?: GeneratedFileInfo
  // Свежий срез контекстного окна — фронт обновляет индикатор без второго
  // запроса в /sessions/{id}/usage.
  context_usage?: ContextUsage
  // Фактически применённый режим памяти ПОСЛЕ серверного резолва/клампа
  // (PR3, TZ_context_window п.7.2): для Pro-моделей с allowed_modes,
  // отличным от полного набора (напр. только ['10k','max']), бэк молча
  // клампит запрошенный режим к ближайшему разрешённому — если это поле
  // отличается от того, что фронт передал в запросе, UI обязан
  // ресинхронизировать выбранный режим по нему (см. Chat.tsx).
  context_mode?: string
  // ok=false + context_overflow=true — не влезает САМО отправленное
  // сообщение (текст + системный промпт + вложения) в окно фикс-режима.
  // Это не «диалог закончился»: историю бэк режет скользящим окном, чат
  // продолжается в любом режиме. Фронт вместо глухой ошибки показывает
  // предложение переслать тот же запрос в режиме '∞' (Chat.tsx →
  // overflowPrompt). required_tokens/limit_tokens — числа для текста.
  context_overflow?: boolean
  required_tokens?: number
  limit_tokens?: number
  // Смета до запуска (владелец 08.10): estimate_confirm — нужно подтверждение (раз на чат), balance_short — баланса
  // не хватает на смету (error — текст «Недостаточно токенов…»). Модель не запускалась, ничего не списано.
  estimate_credits?: number
  estimate_confirm?: boolean
  balance_short?: boolean
  // Баланс ниже порога режима (владелец 15.10, backend services/chat_min_balance): плашка «Сменить режим» / «Пополнить».
  mode_short?: ModeShortInfo | null
  // Ответ дороже баланса (владелец 12.10): открыта часть, остаток — кнопкой «Посмотреть ответ» (unlockMessage).
  locked?: LockedInfo | null
  // id сообщения ответа в истории — по нему «Посмотреть ответ» открывает остаток.
  message_id?: string
  // Карточка «Нарисовать» (backend services/chat_image_card, 16.10): промпт, модели GPT Image с ценой, модель по
  // умолчанию. Приходит только тем, кому режим включён (флаг платформы или staff), остальным в content — ссылка.
  image_card?: ImageCard | null
  // Баланс после списания с сервера — ставить как есть (свой расчёт «до − списано» врёт при параллельных списаниях).
  credits_balance?: number | null
}

/** Ответ чата, открытый частью (backend services/chat_locked): сколько доплатить, какая доля открыта, до какого срока.
 *  Самого остатка на сайте нет — только на сервере, до нажатия «Посмотреть ответ». */
export interface LockedInfo {
  remainder_credits: number
  price_credits: number
  paid_credits: number
  visible_pct: number
  expires_at: string | null
  expired: boolean
  /** Почему ответ вышел дороже баланса (backend chat_locked.PRICE_BASIS): max — память «∞», fact — у модели цена по
   *  факту, fixed — цена известна заранее, но баланс стал меньше, пока нейросеть отвечала. */
  price_basis?: 'max' | 'fact' | 'fixed' | null
  /** Сколько дней хранится остаток (для текста «срок истёк»). */
  keep_days?: number
  /** Тексты карточки из админки «Режимы памяти» (backend chat_locked.TEXTS) с подстановками {price} {paid} {need}
   *  {remainder} {pct} {days}; чего нет — сайт берёт свои стандартные. */
  texts?: Partial<Record<LockedTextKey, string>> | null
}

export type LockedTextKey = 'title' | 'reason_max' | 'reason_fact' | 'reason_fixed' | 'paid' | 'paid_zero' | 'short'
  | 'enough' | 'expired_title' | 'expired_text'

/** Ответ POST /chat/messages/{id}/unlock: ok — весь текст; иначе error (needed — сколько нужно, expired — срок вышел). */
export interface UnlockResult {
  ok: boolean
  content?: string
  credits_spent?: number
  credits_balance?: number
  image_urls?: string[] | null
  file?: GeneratedFileInfo | null
  error?: string
  needed?: number | null
  expired?: boolean | null
}

// Смета сообщения до отправки — POST /api/v1/chat/estimate (тем же правилом, что проверка в POST /chat).
export interface MessageEstimateRequest {
  message: string
  provider: string
  model?: string
  session_id?: string | null
  context_mode?: string
  /** Оценка ещё не загруженных вложений (utils/chatFileEstimate.ts) — файлы грузятся при отправке. */
  attachment_tokens?: number
  has_files?: boolean
  /** Сколько картинок приложено: у модели без зрения — платное описание каждой (describe_credits в смете). */
  image_count?: number
  agent_id?: string
  system_prompt?: string
}

export interface MessageEstimate {
  ok: boolean
  /** Сообщение пойдёт бесплатно по дневному лимиту — смета не нужна. */
  free?: boolean
  /** Списание по факту токенов («∞» или модель «по факту»). */
  fact_priced?: boolean
  credits?: number
  credits_max?: number
  floor?: number
  input_tokens?: number
  output_tokens?: number
  balance?: number
  /** Баланса не хватит — отправка остановится до запуска модели. */
  short?: boolean
  /** Сколько должно быть на балансе, чтобы отправка прошла (худший вход и минимальный ответ). */
  needed?: number
  /** Отправка попросит подтверждение (раз на чат). */
  confirm?: boolean
  context_mode?: string
  /** Надбавка за описание картинок модели без зрения (уже входит в credits/needed) и сколько фото опишут. */
  describe_credits?: number
  describe_photos?: number
  /** Правка приложенных фото в один шаг (21.10): цена правки на модели карточки по умолчанию и войдёт ли она
   *  в цену сообщения (не дороже сметы) — тогда картинка рисуется сразу, без кнопки. */
  edit_price?: number | null
  edit_tolerance_pct?: number
  edit_included?: boolean
  /** Нарисованная в этом чате картинка уйдёт модели (зрячей, пока сообщение с ней в окне памяти) — её вес уже в смете. */
  memory_image?: boolean
}

export async function estimateMessage(request: MessageEstimateRequest): Promise<MessageEstimate | null> {
  try {
    return await api.request('/api/v1/chat/estimate', { method: 'POST', body: request })
  } catch {
    return null
  }
}

// Метаданные приложенных файлов (имя + MIME). Бэк хранит в
// `chat_messages.attached_files_meta` JSONB, фронт показывает в
// списке файлов сессии.
export interface AttachedFileMeta {
  filename: string
  mime_type: string
}

// Картинка как «ссылка на S3-объект». Бэк хранит key, URL генерится
// на лету при выдаче (и через /refresh-attachment-urls). missing=true
// означает что файл реально удалён из MinIO — фронт рисует заглушку.
export interface ImageAttachment {
  key: string
  filename: string
  mime_type?: string
  url?: string       // присутствует если файл существует
  missing?: boolean  // true = файл удалён из хранилища
}

// Ответ /sessions/{id}/refresh-attachment-urls — фронт дёргает на
// img onError для получения свежих URL'ов вместо протухших.
export interface RefreshAttachmentUrlsResponse {
  messages: Record<string, {
    image_attachments?: ImageAttachment[]
    image_urls?: string[]
    generated_file?: GeneratedFileInfo & { missing?: boolean }
  }>
}

export interface ChatSessionItem {
  id: string
  title: string
  model: string | null
  provider: string | null
  message_count: number
  is_pinned: boolean
  created_at: string
  updated_at: string
  agent_id: string | null
  agent_title: string | null
  agent_icon_url: string | null
  // Per-session режим памяти. Источник правды — backend.
  // localStorage больше не используется.
  context_mode: string
  /** Умный помощник среди чатов (23.10): строка — сам помощник, клик открывает его последнюю работу
   *  (/assistants/<слуг>?run=…), а не /chat. */
  kind?: 'chat' | 'assistant'
  /** Только у kind='assistant': адрес помощника и его последняя работа. */
  agent_slug?: string | null
  run_id?: string | null
}

/** Уровень качества модели в карточке и его цена без фото на входе (правила генератора, backend get_credits_price). */
export interface ImageCardLevel {
  quality: string
  price: number
}

/** Модель в карточке «Нарисовать»: id для генератора, подпись на случай, если каталога сайта нет, уровень по умолчанию,
 *  цена уровня по умолчанию, цены всех уровней и надбавка за одно фото на входе (итог = уровень + фото × per_photo). */
export interface ImageCardModel {
  id: string
  label: string
  /** Что в ряду уровней: качество (GPT Image), разрешение (Nano Banana, Seedream) или ряда нет. Значение уровня —
   *  в quality у модели (умолчание) и у каждого levels[]. */
  level_kind?: 'quality' | 'resolution' | 'none'
  quality: string
  price: number
  levels: ImageCardLevel[]
  per_photo: number
  /** Принимает ли фото на входе (правка, референсы) и сколько. Без приёма — только «Нарисовать». */
  supports_input?: boolean
  max_images?: number
  /** Пропорции и форматы на выбор (22.10, «как в виджете») — списки адаптера модели, те же, что в генераторе; на цену
   *  не влияют. Пусто у карточек до 22.10 — тогда настроек нет, сервер берёт умолчание карточки. */
  aspect_ratios?: string[]
  output_formats?: string[]
}

/** Входная картинка карточки: source — правимая (последняя нарисованная в чате), reference — фото из сообщения человека.
 *  url — подписанная ссылка на миниатюру (в базе хранится только ключ). */
export interface ImageCardInput {
  key: string
  role: 'source' | 'reference'
  filename?: string
  url?: string | null
  message_id?: string | null
}

/** Карточка «Нарисовать» / «Изменить картинку» в ответе чата (offer) и пометка сообщения с готовой картинкой (result).
 *  Backend services/chat_image_card. */
export type ImageCard =
  | {
      kind: 'offer'
      mode: 'draw' | 'edit'
      prompt: string
      provider: string
      models: ImageCardModel[]
      default_model: string
      aspect_ratio?: string
      inputs: ImageCardInput[]
      /** Исходник правки: нарисованная в чате картинка (result) или своё фото из сообщения человека (upload, 20.10). */
      source?: { request_id: string | null; message_id: string | null; model: string | null; prompt: string; kind?: 'result' | 'upload' } | null
      /** Правка запущена сервером сразу (21.10): карточка «в работе», картинка придёт в чат; paid_with — общее
       *  списание с ответом чата (в счёт сообщения). */
      started?: { request_id: string; price: number; at: string; paid_with?: string | null } | null
      /** Правка в один шаг не удалась (сервер снял started): карточка снова с кнопкой, повтор платный.
       *  soft — картинка готова, но история не перечиталась: без кнопки, только подсказка обновить страницу. */
      failed?: { error: string; at: string; soft?: boolean } | null
    }
  | {
      kind: 'result'
      mode?: 'draw' | 'edit'
      prompt: string
      model: string | null
      quality?: string | null
      request_id: string
      offer_message_id?: string | null
      source_request_id?: string | null
      /** Картинка дописана в тот же ответ нейросети (правка в один шаг, 21.10). */
      one_step?: boolean
    }

export interface ChatMessageItem {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  // image_urls — массив рабочих URL'ов (свежий presigned, готов к рендеру).
  // image_attachments — расширенная metadata с key/filename/missing для
  // двухступенчатого fallback'а на фронте (refresh → заглушка).
  image_urls: string[] | null
  image_attachments: ImageAttachment[] | null
  generated_file: GeneratedFileInfo | null
  attached_files_meta: AttachedFileMeta[] | null
  tokens_input: number | null
  tokens_output: number | null
  credits_spent: number | null
  model: string | null
  created_at: string
  /** Ответ открыт частью (владелец 12.10) — остаток открывает «Посмотреть ответ». */
  locked?: LockedInfo | null
  /** Карточка «Нарисовать» (offer) или пометка картинки из чата (result). */
  image_card?: ImageCard | null
}

// Ответ /api/v1/chat/sessions/{id}/usage. Используется для:
//   1) Инициализации индикатора при загрузке сессии,
//   2) Превью при смене режима памяти (передаётся `context_mode` query).
export interface SessionUsageResponse extends ContextUsage {
  context_mode: string
  provider: string | null
  messages_in_context: number
  messages_total: number
  summary: {
    present: boolean
    tokens: number
    updated_at: string | null
  }
  breakdown: {
    summary: number
    history_text: number
    history_files: number
    history_images: number
  }
  files: AttachedFileMeta[]
}

export interface SessionDetailResponse {
  session: ChatSessionItem
  messages: ChatMessageItem[]
  // Только при запросе с limit: есть сообщения раньше первого из отданных.
  has_more?: boolean
}

export interface UploadedChatFile {
  url: string
  // S3-ключ объекта в MinIO. Используется в `ChatRequest.image_attachments`
  // / `file_attachments` чтобы бэк не парсил URL заново. URL остаётся для
  // немедленного preview в local state до ответа бэка.
  key: string
  mime_type: string
  name: string
}

export interface TTSResponse {
  ok: boolean
  url?: string
  credits_spent?: number
  error?: string
}

// Остаток бесплатных запросов + цена/скидка для конкретной (provider, model).
// Питает блок над полем ввода: «Осталось бесплатных запросов к этой нейросети
// сегодня: N из M» и отметку сниженной цены. Источник — GET /chat/model-pricing.
export interface ModelPricing {
  provider: string
  model: string
  // has_free=false → бесплатного тира для модели нет, блок остатка не рисуем.
  has_free: boolean
  // Бесплатно ли сейчас по правилу отправки (включая легаси-пул тарифа) — локальный стоп по балансу не нужен.
  free_now?: boolean | null
  free_limit: number | null
  free_remaining: number | null
  // ISO-UTC момент сброса суточного счётчика (полночь UTC) — показываем
  // «обновится в HH:MM» в местном времени юзера.
  free_resets_at: string | null
  // price — итоговая цена запроса в токенах (после подписочной скидки).
  // price_before заполнен только когда есть скидка (для зачёркивания «было»).
  price: number | null
  price_before: number | null
  discount_percent: number | null
  // true → модель тарифицируется по факту токенов (Pro/reasoning), итог может
  // быть выше показанной цены → рисуем «от N». false → в фиксированных режимах
  // памяти (10K..30K) списание строго равно показанной цене.
  // Источник правды — DYNAMIC_PRICED_MODELS на бэке. undefined (старый бэк) →
  // фолбэк на локальный список isDynamicPriced().
  dynamic_priced?: boolean
}

// Ценообразующие параметры генерации — сужают бесплатный лимит до конкретной
// комбинации (та же подпись, какой бэк выбирает строку-лимит при списании).
export interface FreeTierParams {
  resolution?: string | null
  duration?: string | number | null
  aspect_ratio?: string | null
  output_format?: string | null
  quality?: string | null
}

// Остаток/цена для (provider, model). Тихо возвращает null при ошибке и гостю —
// блок остатка необязателен и не должен ломать рендер. type — тракт
// суточного счётчика (chat|image|video), дефолт chat (старый контракт).
export async function getModelPricing(
    provider: string,
    model: string,
    type: 'chat' | 'image' | 'video' = 'chat',
    params?: FreeTierParams | null,
): Promise<ModelPricing | null> {
  // Гостю не спрашиваем: ручка только для вошедших, а на 401 клиент уводит на /login — так с 08.10 страница любой
  // карточки (тренды, изображения) уводила гостя на вход: виджет спрашивал бесплатный остаток модели.
  if (!api.isAuthenticated()) return null
  try {
    const q = new URLSearchParams({ provider, model, type })
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== null && v !== undefined && String(v).trim() !== '') q.set(k, String(v))
    }
    return await api.request(`/api/v1/chat/model-pricing?${q.toString()}`)
  } catch {
    return null
  }
}

// Витрина режимов памяти для (provider, model) — PR3, TZ_context_window.
// Источник данных — DB-конфигурация (context_mode_settings/
// model_context_overrides), с фоллбэком на бэковый дефолт при пустой/
// недоступной таблице. allowed — какие режимы реально разрешены ЭТОЙ модели
// (Pro-модели могут быть ограничены подмножеством, напр. ['10k','max']).
export interface ContextModeOption {
  mode_key: string
  label: string
  token_limit: number | null
  multiplier: number
  max_output_tokens: number
  is_dynamic: boolean
  allowed: boolean
  price: number | null
  price_before: number | null
  // true → `price` это НИЖНЯЯ ГРАНИЦА («от N»): списание считается по факту
  // токенов и может уйти выше (режим «∞» и модели с pricing_kind='dynamic').
  // false → точная фиксированная цена режима.
  price_is_minimum?: boolean
  // Минимум на балансе для отправки в этом режиме (только динамические; null — порога нет). Админка «Режимы памяти».
  min_balance?: number | null
}

export interface ContextModesResponse {
  provider: string
  model: string
  pricing_kind: 'fixed' | 'dynamic'
  allowed_modes: string[]
  modes: ContextModeOption[]
  source: 'db' | 'fallback_hardcoded'
  // Текст плашки порога с подстановками {mode} {need} {balance} — правится в админке.
  min_balance_text?: string
}

/** Отказ до отправки: баланс ниже порога режима (need) или цены статичного окна. */
export interface ModeShortInfo {
  mode: string
  label: string
  need: number
  balance: number
}

// Тихо возвращает null при ошибке — вызывающий (ContextButton) обязан
// откатиться на офлайн-фоллбэк констант, а не сломать рендер.
export async function getContextModes(provider: string, model: string): Promise<ContextModesResponse | null> {
  try {
    const q = new URLSearchParams({ provider, model })
    return await api.request(`/api/v1/chat/context-modes?${q.toString()}`)
  } catch {
    return null
  }
}

// Уникальный ключ идемпотентности на каждый клик «Отправить».
export function genIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fallthrough на ручной генератор */
  }
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

// UUID НОВОЙ сессии чата, сгенерированный КЛИЕНТОМ заранее. Отдаём бэку в
// session_id, чтобы id был известен до ответа (URL ставится сразу) → при потере
// ответа refresh /chat/:id и error-recovery подтянут сохранённый ответ. Fallback
// тоже UUIDv4 — иначе бэк не примет id (валидирует UUID).
export function genSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fallthrough на ручной UUIDv4 */
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export async function sendMessage(request: ChatRequest, idempotencyKey: string = genIdempotencyKey()): Promise<ChatResponse> {
  // Идемпотентность: уникальный ключ на каждый вызов (клик). Сетевые ретраи
  // того же fetch (Chrome HTTP/2 retry, обрыв мобильного, DPI cut) транспорт
  // переотправляет с тем же заголовком → бэк не спишет повторно.
  // См. backend chat.py / utils/idempotency.py. Ключ можно задать снаружи —
  // по нему же getPendingReply найдёт ИМЕННО этот запрос, если связь оборвётся.
  return api.request('/api/v1/chat', {
    method: 'POST',
    // client_features: сайт умеет ответ, открытый частью (карточка «Посмотреть ответ») — без пометки для старых вкладок
    body: { ...request, client_features: ['locked'] },
    headers: { 'X-Idempotency-Key': idempotencyKey },
    // Чат показывает нехватку токенов сам — строкой с кнопкой «Пополнить баланс» под сообщением.
    silentInsufficient: true,
  })
}

// «Посмотреть ответ — N ✦» (владелец 12.10): списание только по этому нажатию человека.
/** «Изменить эту картинку» под любой нарисованной в чате картинкой (фаза 3): сервер кладёт в историю карточку
 *  «Изменить картинку» с пустой инструкцией — без обращения к модели чата и без списания. */
export async function createImageCard(sessionId: string, messageId: string): Promise<ChatMessageItem> {
  return api.request(`/api/v1/chat/sessions/${sessionId}/image-card`, { method: 'POST', body: { message_id: messageId } })
}

/** Режимы карточки картинок в чате (backend services/chat_image_onestep, 21.10): enabled — карточка доступна этому
 *  человеку (флаг платформы или сотрудник); auto — правка фото рисуется сразу в счёт сообщения; quote — формула цены
 *  правки на модели карточки по умолчанию (подсказка у сметы и подтверждение цены при многих фото); tolerance — на
 *  сколько ✦ правка может быть дороже сообщения и всё равно идти сразу. */
export interface ImageCardsStatus {
  enabled: boolean
  auto?: boolean
  quote?: { model: string; label: string; quality: string | null; base: number; per_photo: number; max_images: number } | null
  /** Допуск в процентах от цены сообщения (услышано у владельца: «на пару токенов меньше — не страшно»). */
  tolerance_pct?: number
}

export async function getImageCardsStatus(): Promise<ImageCardsStatus> {
  return api.request('/api/v1/chat/image-cards')
}

export async function unlockMessage(messageId: string): Promise<UnlockResult> {
  // Нехватку карточка объясняет сама (и сама предлагает «Пополнить») — без общего тоста с другой кнопкой.
  return api.request(`/api/v1/chat/messages/${messageId}/unlock`, { method: 'POST', silentInsufficient: true })
}

export async function getSessions(limit = 50, offset = 0): Promise<ChatSessionItem[]> {
  return api.request(`/api/v1/chat/sessions?limit=${limit}&offset=${offset}`)
}

// Сколько сообщений отдаёт одна страница истории чата (последние; раньше — по прокрутке вверх).
export const SESSION_PAGE_SIZE = 40

export async function getSession(
  sessionId: string,
  page: { limit?: number; before?: string } = { limit: SESSION_PAGE_SIZE },
): Promise<SessionDetailResponse> {
  // features=locked — сайт умеет ответ, открытый частью (без пометки «обновите страницу» для старых вкладок)
  const qs = new URLSearchParams({ features: 'locked' })
  if (page.limit) qs.set('limit', String(page.limit))
  if (page.before) qs.set('before', page.before)
  return api.request(`/api/v1/chat/sessions/${sessionId}?${qs}`)
}

// Что с ответом на последний вопрос сессии: связь с POST /chat могла оборваться
// (Astra думает и дольше 5 минут — прокси закрывает соединение), а бэк работает дальше.
export interface PendingReply {
  status: 'processing' | 'completed' | 'failed' | 'none'
  error?: string | null
  credits_balance?: number | null
}

/** `idempotencyKey` — ключ отправки (sendMessage): статус именно этого запроса; без него — последнего в сессии. */
export async function getPendingReply(sessionId: string, idempotencyKey?: string): Promise<PendingReply> {
  const query = idempotencyKey ? `?key=${encodeURIComponent(idempotencyKey)}` : ''
  return api.request(`/api/v1/chat/sessions/${sessionId}/pending${query}`)
}

export async function createSession(data: {
  title?: string
  model?: string
  provider?: string
}): Promise<ChatSessionItem> {
  return api.request('/api/v1/chat/sessions', {
    method: 'POST',
    body: data,
  })
}

export async function updateSession(
    sessionId: string,
    data: { title?: string; is_pinned?: boolean; context_mode?: string },
): Promise<ChatSessionItem> {
  return api.request(`/api/v1/chat/sessions/${sessionId}`, {
    method: 'PATCH',
    body: data,
  })
}

// Запрос свежей оценки заполнения контекста. `contextMode` передаётся
// для preview-сценария (юзер тащит slider памяти — показываем что будет
// в новом режиме до фиксации). Без параметра — берётся сохранённый.
export async function getSessionUsage(
    sessionId: string,
    contextMode?: string,
): Promise<SessionUsageResponse> {
  const qs = contextMode ? `?context_mode=${encodeURIComponent(contextMode)}` : ''
  return api.request(`/api/v1/chat/sessions/${sessionId}/usage${qs}`)
}

// Получить свежие presigned URL'ы для всех вложений сессии. Используется
// фронтом когда `<img onError>` срабатывает (URL протух или вкладка висела
// долго). Если после refresh URL всё ещё битый (или missing=true) — фронт
// рендерит заглушку «🚫 изображение недоступно».
export async function refreshSessionAttachmentUrls(
    sessionId: string,
): Promise<RefreshAttachmentUrlsResponse> {
  return api.request(`/api/v1/chat/sessions/${sessionId}/refresh-attachment-urls`)
}

export async function deleteSession(sessionId: string): Promise<{ ok: boolean }> {
  return api.request(`/api/v1/chat/sessions/${sessionId}`, {
    method: 'DELETE',
  })
}

export async function uploadChatFile(file: File): Promise<UploadedChatFile> {
  const formData = new FormData()
  formData.append('file', file)

  const token = localStorage.getItem('token')
  const API_URL =
      import.meta.env.VITE_API_URL ||
      (typeof window !== 'undefined' && window.location.hostname === 'localhost'
          ? 'http://localhost:8100'
          : '')

  const isImage = file.type.startsWith('image/')
  const category = isImage ? 'images' : 'uploads'
  const resp = await fetch(`${API_URL}/api/v1/files/upload?category=${category}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  })

  if (!resp.ok) {
    throw new Error('Upload failed')
  }

  const data = await resp.json()

  return {
    url: data.url,
    key: data.key,  // S3-ключ для надёжной работы с object'ом без парсинга URL.
    mime_type: data.mime_type || file.type || 'application/octet-stream',
    name: data.name || file.name,
  }
}

export async function ttsGenerate(text: string, voice?: string): Promise<TTSResponse> {
  return api.request('/api/v1/tts/generate', {
    method: 'POST',
    body: { text, voice: voice || 'nova' },
  })
}